import React, { useEffect, useState, useCallback, useRef } from 'react';
import { GetRequest, PostRequest } from 'components/common';

const GoogleSheetsPicker = ({ dataSourceId, onSpreadsheetsPicked, onCancel }) => {
  const [pickerConfig, setPickerConfig] = useState(null);
  const [pickerLoaded, setPickerLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false); // Show loading while registering spreadsheets
  const pickerShownRef = useRef(false);

  // Fetch picker configuration and access token from backend
  useEffect(() => {
    GetRequest({
      url: `/quote-builder/data-sources/${dataSourceId}/picker-token`,
      callback: (data) => {
        setPickerConfig(data);
        if (!data.api_key || !data.access_token) {
          setError('Google Picker is not configured. Please contact support.');
          setIsLoading(false);
        }
      },
      401: (data) => {
        if (data?.needs_reauth) {
          setError('Your Google connection has expired. Please reconnect your Google account in Settings.');
        } else {
          setError('Not authorized. Please log in again.');
        }
        setIsLoading(false);
      },
      422: () => {
        setError('Failed to load picker configuration');
        setIsLoading(false);
      }
    });
  }, [dataSourceId]);

  // Load Google API script
  useEffect(() => {
    const loadGapiScript = () => {
      if (window.gapi && window.gapi.load) {
        window.gapi.load('picker', () => setPickerLoaded(true));
        return;
      }

      const existingScript = document.querySelector('script[src="https://apis.google.com/js/api.js"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          window.gapi.load('picker', () => setPickerLoaded(true));
        });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.gapi.load('picker', () => setPickerLoaded(true));
      };
      script.onerror = () => {
        setError('Failed to load Google API');
        setIsLoading(false);
      };
      document.body.appendChild(script);
    };

    loadGapiScript();
  }, []);

  const showPicker = useCallback(() => {
    if (!pickerLoaded || !pickerConfig || !pickerConfig.access_token || pickerShownRef.current) return;

    pickerShownRef.current = true;
    setIsLoading(false);

    // Save scroll position before opening picker (Google Picker can cause scroll jump)
    const savedScrollY = window.scrollY;
    const savedScrollX = window.scrollX;

    // Create a view for Google Sheets only
    const view = new google.picker.DocsView(google.picker.ViewId.SPREADSHEETS);
    view.setMimeTypes('application/vnd.google-apps.spreadsheet');
    view.setMode(google.picker.DocsViewMode.LIST);

    const picker = new google.picker.PickerBuilder()
      .addView(view)
      .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(pickerConfig.access_token)
      .setDeveloperKey(pickerConfig.api_key)
      .setAppId(pickerConfig.app_id)
      .setTitle('Select spreadsheets to use')
      .setCallback((data) => {
        if (data.action === google.picker.Action.PICKED) {
          const documents = data.docs || [];
          const spreadsheets = documents.map(doc => ({
            id: doc.id,
            name: doc.name,
            url: doc.url
          }));

          // Show loading while registering spreadsheets
          setIsSaving(true);

          // Register picked spreadsheets with backend
          PostRequest({
            url: `/quote-builder/data-sources/${dataSourceId}/pick-spreadsheets`,
            data: { spreadsheets },
            callback: (response) => {
              onSpreadsheetsPicked(response.all_spreadsheets || []);
            },
            422: (response) => {
              setIsSaving(false);
              setError(`Failed to register spreadsheets: ${response.error || 'Unknown error'}`);
            }
          });
        } else if (data.action === google.picker.Action.CANCEL) {
          onCancel?.();
        }
      })
      .build();

    picker.setVisible(true);

    // Restore scroll position after picker opens (prevents violent scroll)
    requestAnimationFrame(() => {
      window.scrollTo(savedScrollX, savedScrollY);
    });
  }, [pickerLoaded, pickerConfig, dataSourceId, onSpreadsheetsPicked, onCancel]);

  // Show picker when config is loaded and picker API is ready
  useEffect(() => {
    if (pickerConfig && pickerConfig.access_token && pickerLoaded) {
      showPicker();
    }
  }, [pickerConfig, pickerLoaded, showPicker]);

  // Centered overlay style for loading/error states
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10001
  };

  const boxStyle = {
    backgroundColor: 'white',
    padding: '20px 30px',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    textAlign: 'center'
  };

  if (error) {
    return (
      <div style={overlayStyle}>
        <div style={boxStyle}>
          <div style={{ color: '#dc3545', marginBottom: '15px' }}>
            <i className="fa fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
            {error}
          </div>
          <button
            className="btn btn-default"
            onClick={onCancel}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={overlayStyle}>
        <div style={boxStyle}>
          <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
          Loading Google Picker...
        </div>
      </div>
    );
  }

  // Show loading while saving picked spreadsheets
  if (isSaving) {
    return (
      <div style={overlayStyle}>
        <div style={boxStyle}>
          <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
          Please wait...
        </div>
      </div>
    );
  }

  return null;
};

export default GoogleSheetsPicker;
